/**
 * Tier-4 hit rules (M21, docs/TOWERS_V2.md "Tier 4"): what a tier-4 tower's hit does beyond its damage, and the
 * per-shot context (every-Nth-shot stuns, a lance bolt's first enemy) the combat system opens around each shot.
 */
import { DamageType, TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { SimConstants } from "../constants";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { canBurn } from "../affixes";
import { applyDamage } from "../systems/combat";
import { igniteBurn } from "../systems/enemyMotion";
import { branchOf, skill } from "./ascension";
import { applyDot, stun } from "./statuses";

/** Damage large enough to finish any enemy through any resistance: an execute kills outright. */
const EXECUTE_OVERKILL = 100;
/** A Glacier's Shatter counts an enemy this slowed (or stunned) as frozen. */
const FROZEN_SLOW = Fix64.parse("0.5");
/** Burn stacks a Gatling's Incendiary Rounds may hold with other fire, as a Fire tower's. */
const INCENDIARY_STACKS = 3;

/** The shot being fired: its tower, the stun every one of its hits carries (0 = none), and whether no enemy is hit yet. */
export interface ShotContext {
  towerId: number;
  stunTicks: number;
  first: boolean;
  /** Set while tier 4's periodic damage runs (poison, zones, auras, spiders, phoenixes): no Burrower dives on it (ENEMIES_V2). */
  periodic: boolean;
}

/**
 * Opens a shot for `t` (combat.fireTowers calls it before the hits and `endShot` after): counts the shot, and arms the
 * every-Nth-shot stuns (Bombard Shockwave, Storm Thunderclap) when this shot is the Nth.
 */
export function beginShot(sim: LevelSim, t: Tower): void {
  const ctx = sim.shot;
  ctx.towerId = t.id; ctx.stunTicks = 0; ctx.first = true;
  if (branchOf(sim, t) === null) return;
  t.shotCount++;
  const s = skill(sim, t, "shockwave") ?? skill(sim, t, "thunderclap");
  if (s !== null && t.shotCount % Math.max(1, s.params.int("everyShots", 1)) === 0) {
    ctx.stunTicks = s.params.int("stunTicks");
    sim.events.add(SimEventKind.SkillFired, t.id, s.index, 0);
  }
}

export function endShot(sim: LevelSim): void { sim.shot.towerId = 0; sim.shot.stunTicks = 0; sim.shot.first = false; }

/**
 * A tier-4 tower's hit, before its damage lands: returns the damage to deal (Shatter, Static Field, Mana Burn raise it)
 * and applies what rides on the hit — the shot's stun, Impale on a bolt's first enemy, Deep Freeze, poison and bleed,
 * Incendiary burns, Meltdown and Armour Breaker shred.
 */
export function branchHit(sim: LevelSim, e: Enemy, t: Tower, a: TowerArchetype, damage: Fix64): Fix64 {
  const b = branchOf(sim, t);
  if (b === null) return damage;
  const ctx = sim.shot;
  if (ctx.towerId === t.id) {
    if (ctx.stunTicks > 0) stun(sim, e, t, ctx.stunTicks);
    if (ctx.first) {
      ctx.first = false;
      const impale = skill(sim, t, "impale");
      if (impale !== null) { stun(sim, e, t, impale.params.int("stunTicks")); sim.events.add(SimEventKind.SkillFired, t.id, impale.index, e.id); }
    }
  }
  let out = damage;
  switch (b.id) {
    case "glacier": {
      const freeze = skill(sim, t, "deepFreeze");
      const shatter = skill(sim, t, "shatter");
      const frozen = e.stunTicksLeft > 0 || (e.slowTicksLeft > 0 && e.slowFraction.ge(FROZEN_SLOW));
      if (shatter !== null && frozen) out = out.mul(shatter.params.num("bonusMult", Fix64.One));
      if (freeze !== null && (e.hitsTaken + 1) % Math.max(1, freeze.params.int("everyHits", 1)) === 0) {
        stun(sim, e, t, freeze.params.int("stunTicks"));
        sim.events.add(SimEventKind.SkillFired, t.id, freeze.index, e.id);
      }
      break;
    }
    case "ranger": dot(sim, e, t, skill(sim, t, "poisonArrows"), damage); break;
    case "repeater": dot(sim, e, t, skill(sim, t, "barbedBolts"), damage); break;
    case "gatling": {
      const s = skill(sim, t, "incendiary");
      if (s !== null && canBurn(sim, e)) {
        igniteBurn(e, t.id, damage.mul(s.params.num("burnPerSecondMult")).divInt(SimConstants.TicksPerSecond), s.params.int("burnTicks"), INCENDIARY_STACKS);
        sim.events.add(SimEventKind.EnemyBurning, e.id, t.id, s.params.int("burnTicks"));
      }
      break;
    }
    case "inferno": shred(sim, e, t, skill(sim, t, "meltdown")); break;
    case "lance": shred(sim, e, t, skill(sim, t, "armourBreaker")); break;
    case "tesla": {
      const s = skill(sim, t, "staticField");
      if (s !== null && e.isAir) out = out.mul(Fix64.One.add(s.params.num("airBonus")));
      break;
    }
    case "sorcerer": {
      const s = skill(sim, t, "manaBurn");
      if (s !== null) out = out.mul(Fix64.One.add(s.params.num("damageBonus")));
      break;
    }
    default: break;
  }
  return out;
}

/**
 * After a tier-4 hit's damage: the executes — Marksman Headshot (any hit), Disintegrator Annihilate (a full-ramp pulse) —
 * kill a non-boss left under the skill's share of its health (EnemyExecuted, then its EnemyKilled).
 */
export function branchAfterHit(sim: LevelSim, e: Enemy, t: Tower): void {
  if (!e.alive || e.isBoss) return;
  const b = branchOf(sim, t);
  if (b === null) return;
  let s = null;
  if (b.id === "marksman") s = skill(sim, t, "headshot");
  else if (b.id === "disintegrator" && sim.beamRamp(t).ge(Fix64.One)) s = skill(sim, t, "annihilate");
  if (s === null || e.hp.ge(e.maxHp.mul(s.params.num("executeBelow")))) return;
  execute(sim, e, t, s.index);
}

/** Kills `e` outright for tower `t`'s skill (EnemyExecuted, then the kill's own events). */
export function execute(sim: LevelSim, e: Enemy, t: Tower, skillIndex: number): void {
  sim.events.add(SimEventKind.EnemyExecuted, e.id, t.id, skillIndex);
  sim.events.add(SimEventKind.SkillFired, t.id, skillIndex, e.id);
  applyDamage(sim, e, t, e.hp.mulInt(EXECUTE_OVERKILL).add(Fix64.One), DamageType.Magic);
}

function dot(sim: LevelSim, e: Enemy, t: Tower, s: ReturnType<typeof skill>, damage: Fix64): void {
  if (s === null) return;
  applyDot(e, t, damage.mul(s.params.num("dotPerSecondMult")).divInt(SimConstants.TicksPerSecond), s.params.int("dotTicks"));
}

function shred(sim: LevelSim, e: Enemy, t: Tower, s: ReturnType<typeof skill>): void {
  if (s === null || e.shredStacks >= s.params.int("shredMax")) return;
  e.shredStacks++;
  e.shredArmour = e.shredArmour.add(s.params.num("shredPerHit"));
  sim.events.add(SimEventKind.EnemyShredded, e.id, t.id, e.shredStacks);
}

/** Eagle Eye: the tower can target a burrowed enemy (the enemy side's burrow rule asks this). */
export function seesBurrowed(sim: LevelSim, t: Tower): boolean { return (skill(sim, t, "eagleEye")?.params.int("seesBurrowed") ?? 0) > 0; }
/** Mana Burn: the tower's magic ignores a Spellward's rune shield (the enemy side's resist rule asks this). */
export function piercesWard(sim: LevelSim, t: Tower | null): boolean { return t !== null && (skill(sim, t, "manaBurn")?.params.int("piercesWard") ?? 0) > 0; }
